import { useMemo } from 'react';
import {
  EditableFormTemplate,
  EditableFormTemplateField,
  EditableFormTemplateFieldGroup,
  isTriggerField,
  TriggerField,
} from '../types';

type SetOfIds = Set<string>;
type MapOfIdToField = Map<string, EditableFormTemplateField>;
type MapOfIdToTriggerField = Map<string, TriggerField>;

export type FieldConditionHelpers = {
  getFieldById: (fieldId: string) => EditableFormTemplateField | undefined;
  getTriggerFieldByConditionallyShownFieldId: (
    fieldId: string,
  ) => TriggerField | undefined;
  isConditionTriggerField: (fieldId: string) => boolean;
};

function addConditionalFieldInfoForFieldsInThisGroup(
  group: EditableFormTemplateFieldGroup,
  {
    fieldsById,
    triggerFieldByConditionallyShownFieldId,
    conditionalTriggerFields,
  }: {
    fieldsById: MapOfIdToField;
    triggerFieldByConditionallyShownFieldId: MapOfIdToTriggerField;
    conditionalTriggerFields: SetOfIds;
  },
) {
  group.fields.forEach((field) => {
    fieldsById.set(field.id, field);

    if (isTriggerField(field)) {
      conditionalTriggerFields.add(field.id);
      field.conditionalRules.forEach((rule) => {
        rule.conditionallyShownFields.forEach(({ id }) => {
          triggerFieldByConditionallyShownFieldId.set(id, field);
        });
      });
    }
  });
}

export const useFieldConditionHelpers = (
  formTemplate: EditableFormTemplate | undefined,
): FieldConditionHelpers =>
  useMemo(() => {
    const fieldsById: MapOfIdToField = new Map();
    const triggerFieldByConditionallyShownFieldId: MapOfIdToTriggerField =
      new Map();
    const conditionalTriggerFields = new Set<string>();

    formTemplate?.groups.forEach((group) => {
      addConditionalFieldInfoForFieldsInThisGroup(group, {
        fieldsById,
        triggerFieldByConditionallyShownFieldId,
        conditionalTriggerFields,
      });
    });

    const [
      getFieldById,
      getTriggerFieldByConditionallyShownFieldId,
      isConditionTriggerField,
    ] = [
      (fieldId: string) => fieldsById.get(fieldId),
      (fieldId: string) => triggerFieldByConditionallyShownFieldId.get(fieldId),
      (fieldId: string) => conditionalTriggerFields.has(fieldId),
    ];

    return {
      getFieldById,
      getTriggerFieldByConditionallyShownFieldId,
      isConditionTriggerField,
    };
  }, [formTemplate]);
